import { ConnectButton } from "@rainbow-me/rainbowkit";
import {
  ArrowUpRight,
  BookOpen,
  ChartNoAxesColumnIncreasing,
  Heart,
  LayoutGrid,
  Menu,
  Plus,
  ReceiptText,
  Wallet,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Logo } from "./Logo";

const links = [
  { to: "/", label: "Overview", icon: LayoutGrid },
  { to: "/market", label: "Markets", icon: ChartNoAxesColumnIncreasing },
  { to: "/causes", label: "Find a cause", icon: Heart },
  { to: "/ledger", label: "Impact ledger", icon: ReceiptText },
] as const;

export function Header() {
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const location = useLocation();
  const title = location.pathname.startsWith("/token/")
    ? "Market detail"
    : {
        "/": "Overview",
        "/market": "Markets",
        "/causes": "Cause directory",
        "/ledger": "Impact ledger",
        "/launch": "Create a token",
        "/about": "The idea",
      }[location.pathname] || "Overview";
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [open]);

  return (
    <>
      {open && (
        <button
          className="nav-scrim"
          aria-label="Close navigation"
          onClick={() => setOpen(false)}
        />
      )}
      <aside
        className={`sidebar ${open ? "is-open" : ""}`}
        id="site-navigation"
      >
        <div className="sidebar-brand">
          <Logo />
          <button
            className="menu-button sidebar-close"
            aria-label="Close navigation"
            onClick={() => {
              setOpen(false);
              menuButton.current?.focus();
            }}
          >
            <X size={20} />
          </button>
        </div>
        <span className="sidebar-label">A MARKET FOR GOOD</span>
        <nav className="primary-nav" aria-label="Primary navigation">
          {links.map(({ to, label, icon: Icon }) => (
            <NavLink
              end={to === "/"}
              to={to}
              key={to}
              onClick={() => setOpen(false)}
            >
              <Icon size={19} strokeWidth={1.6} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <Link
          className="sidebar-launch"
          to="/launch"
          onClick={() => setOpen(false)}
        >
          <Plus size={18} />
          Create a token
          <ArrowUpRight size={17} />
        </Link>
        <div className="sidebar-bottom">
          <div className="sidebar-note">
            <span className="mini-mark" aria-hidden="true">
              ↗
            </span>
            <p>
              Turn a community
              <br />
              into a contribution.
            </p>
            <Link to="/about" onClick={() => setOpen(false)}>
              Meet Kyndo <ArrowUpRight size={15} />
            </Link>
          </div>
          <NavLink
            className="sidebar-about"
            to="/about"
            onClick={() => setOpen(false)}
          >
            <BookOpen size={17} />
            How it works
          </NavLink>
          <div className="sidebar-network">
            <span>
              <i />
              Built on Base
            </span>
            <span className="mono">8453</span>
          </div>
        </div>
      </aside>
      <header className="topbar">
        <div className="topbar-location">
          <button
            ref={menuButton}
            className="menu-button"
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
            aria-controls="site-navigation"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
          <Link className="mobile-wordmark" to="/">
            kyndo.
          </Link>
          <span className="desktop-breadcrumb">
            Workspace <span>/</span>
          </span>
          <strong>{title}</strong>
        </div>
        <div className="topbar-actions">
          <span className="network-badge">
            <i />
            Base mainnet
          </span>
          <ConnectButton.Custom>
            {({
              account,
              chain,
              mounted,
              openAccountModal,
              openChainModal,
              openConnectModal,
            }) => {
              const connected = mounted && account && chain;
              const unsupported = connected && chain.unsupported;
              return (
                <button
                  className="wallet-button"
                  type="button"
                  onClick={
                    unsupported
                      ? openChainModal
                      : connected
                      ? openAccountModal
                      : openConnectModal
                  }
                  disabled={!mounted}
                  aria-label={
                    unsupported
                      ? "Switch wallet to Base"
                      : connected
                      ? `Wallet ${account.displayName}`
                      : "Connect wallet"
                  }
                >
                  <Wallet size={16} />
                  <span>
                    {unsupported
                      ? "Switch to Base"
                      : connected
                      ? account.displayName
                      : "Connect wallet"}
                  </span>
                </button>
              );
            }}
          </ConnectButton.Custom>
        </div>
      </header>
    </>
  );
}
